import {
  Brain, ShieldCheck, Radar, Link2, Sparkles, Trophy, Wallet,
  Gauge, Percent, TrendingUp, Database, Coins, LineChart, MessageCircleQuestion,
  Search, ListChecks, MessageSquareText, UserCheck, Sparkle,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/** P0.56 (Sección 15 del encargo) -- onboarding premium en vez de glosario técnico: los 5
 * pasos son una secuencia REAL (así es como John Anthony trabaja cada día, en ese orden),
 * así que la numeración 01-05 codifica información real (ver frontend-design: "numbered
 * markers... only if content actually is a sequence"), no es decoración. */
const PROCESS_STEPS = [
  { n: '01', icon: Search, title: 'Analizamos', body: 'Cada partido con cobertura soportada se evalúa con modelos estadísticos reales sobre datos históricos y de mercado.' },
  { n: '02', icon: ListChecks, title: 'Seleccionamos', body: 'Solo pasan las oportunidades que superan los filtros de probabilidad, valor y calidad de información -- la mayoría se descarta.' },
  { n: '03', icon: MessageSquareText, title: 'Explicamos', body: 'Cada selección incluye por qué se recomienda, con qué datos, y qué podría hacerla fallar. Nunca una caja negra.' },
  { n: '04', icon: UserCheck, title: 'Tú decides', body: 'John Anthony analiza y explica -- la decisión de apostar, cuánto y dónde, siempre es tuya.' },
  { n: '05', icon: Sparkle, title: 'Aprendemos de ti', body: 'BLACK -- registra tus propias apuestas y John Anthony analiza tu comportamiento real: categorías, cuotas, drawdown, patrones.', black: true },
]

/** Sección 4/27 del encargo P0.23 -- "¿Qué ofrece John Anthony?" + definiciones de cada
 * métrica, reemplazando la antigua página "Ayuda" mínima. Copy alineado al mockup visual
 * maestro aprobado, sin prometer rentabilidad garantizada (Sección 1/27: nunca "100%
 * seguro"/"ganancia segura"). */
const CATEGORIES = [
  {
    icon: Brain, tone: 'text-brand bg-brand-soft/40', title: 'Top 3 IA',
    body: 'Las oportunidades con mejor equilibrio entre probabilidad, valor y calidad de información detectadas por el sistema.',
  },
  {
    icon: ShieldCheck, tone: 'text-emerald-400 bg-emerald-500/10', title: 'Top 3 Seguro',
    body: 'Selecciones donde John Anthony prioriza probabilidad y estabilidad sobre retorno potencial.',
  },
  {
    icon: Radar, tone: 'text-cyan bg-cyan/10', title: 'Picks Secundarios',
    body: 'Oportunidades adicionales que superan los filtros del sistema pero quedan fuera de las selecciones principales.',
  },
  // P0.26 (Sección 18 del encargo): explicación independiente por cada tamaño de Parlay,
  // en vez de una sola frase genérica para los tres.
  // Sprint "Parlay Official Final" (25/09/2026): renombrado a JA Duo/Triple/Quad -- probability
  // first, sin semántica de riesgo por nombre (ver `ParlayCard.tsx`). Piernas corregidas a los
  // valores reales (`ParlayOptimizer.PARLAY_*`, n_legs_options exacto): 2/3/4, nunca 5.
  {
    icon: Link2, tone: 'text-parlay bg-parlay-soft', title: 'JA Duo',
    body: '2 selecciones. La combinación de mayor probabilidad conjunta real que cumple la cuota mínima combinada.',
  },
  {
    icon: Link2, tone: 'text-parlay bg-parlay-soft', title: 'JA Triple',
    body: '3 selecciones. La combinación de mayor probabilidad conjunta real que cumple la cuota mínima combinada.',
  },
  {
    icon: Link2, tone: 'text-parlay bg-parlay-soft', title: 'JA Quad',
    body: '4 selecciones. La combinación de mayor probabilidad conjunta real que cumple la cuota mínima combinada.',
  },
  {
    icon: Sparkles, tone: 'text-violet bg-violet/10', title: 'Same Game Parlay',
    body: 'Combinación especial de mercados del mismo partido, solo cuando existe suficiente información y una construcción coherente entre esos mercados.',
  },
  {
    icon: Trophy, tone: 'text-yellow-400 bg-yellow-950/30', title: 'Reto John Anthony',
    body: 'Estrategia semanal oficial con máximo una selección por día cuando existe una oportunidad adecuada.',
  },
  {
    icon: Wallet, tone: 'text-brand bg-brand-soft/40', title: 'Mi Banca',
    body: 'Herramienta para registrar capital, movimientos, resultados y evolución de tus estrategias.',
  },
]

const METRICS = [
  {
    icon: Percent, title: 'Probabilidad estimada',
    body: 'La chance que el modelo calcula para una selección concreta, a partir de datos históricos y del contexto del partido.',
  },
  {
    icon: Gauge, title: 'Confianza',
    body: 'Cuánto respaldan los datos disponibles a esa probabilidad (consenso entre modelos, tamaño de muestra, calidad de la información). No es lo mismo que la probabilidad -- puede haber alta probabilidad con confianza media, o viceversa.',
  },
  {
    icon: TrendingUp, title: 'Riesgo',
    body: 'Evalúa cuán expuesta está la selección a factores de incertidumbre (volatilidad del mercado, datos limitados, contexto del partido).',
  },
  {
    icon: Database, title: 'Calidad de datos',
    body: 'Cuánta información real respalda el análisis de este partido/mercado -- nunca se inventa un dato cuando falta.',
  },
  {
    icon: Coins, title: 'Cuota',
    body: 'El precio real ofrecido por las casas de apuestas para una selección. La "cuota justa" es la que el modelo calcularía sin margen, a partir de su propia probabilidad.',
  },
  {
    icon: LineChart, title: 'ROI',
    body: 'Rendimiento sobre lo apostado, calculado exclusivamente con apuestas ya liquidadas. Nunca incluye depósitos ni retiros de banca.',
  },
]

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <p className="text-[10px] font-black uppercase tracking-wider text-text-dim px-1">{title}</p>
      {children}
    </section>
  )
}

export function HelpPage() {
  return (
    <div className="p-4 space-y-6">
      <div className="space-y-1.5">
        <h1 className="text-lg font-black text-text">Cómo funciona</h1>
        <p className="text-xs text-text-dim leading-relaxed">
          John Anthony es una plataforma de inteligencia deportiva: analiza partidos, estima probabilidades,
          compara precio y valor, evalúa riesgo, construye estrategias y aprende del histórico. No es una promesa
          de ganancia -- es análisis con datos reales, siempre explicado.
        </p>
      </div>

      <Section title="Cómo trabaja John Anthony">
        <div className="space-y-0">
          {PROCESS_STEPS.map(({ n, icon: Icon, title, body, black }, i) => (
            <div key={n} className={cn('flex gap-4 py-3.5', i < PROCESS_STEPS.length - 1 && 'border-b border-border/40')}>
              <div className="flex flex-col items-center gap-1.5 shrink-0 w-9">
                <span className={cn('figure text-[11px] font-bold', black ? 'text-exact' : 'text-text-dim')}>{n}</span>
                <span className={cn('h-8 w-8 rounded-full flex items-center justify-center border', black ? 'text-exact border-exact/40 bg-exact-soft/20' : 'text-text-dim border-border bg-panel-elevated')}>
                  <Icon className="h-4 w-4" />
                </span>
              </div>
              <div className="space-y-0.5 pt-0.5">
                <p className="text-sm font-bold text-text flex items-center gap-1.5">
                  {title}
                  {black && <span className="text-[9px] font-black uppercase tracking-wider text-exact border border-exact/30 rounded-full px-1.5 py-0.5">Black</span>}
                </p>
                <p className="text-xs text-text-muted leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="¿Qué ofrece John Anthony?">
        <div className="grid grid-cols-1 gap-2.5">
          {CATEGORIES.map(({ icon: Icon, tone, title, body }) => (
            <div key={title} className="card flex gap-3 p-3.5">
              <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${tone}`}>
                <Icon className="h-4.5 w-4.5" />
              </span>
              <div className="space-y-0.5 min-w-0">
                <p className="text-sm font-bold text-text">{title}</p>
                <p className="text-xs text-text-muted leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Cómo interpretar cada métrica">
        <div className="grid grid-cols-1 gap-2.5">
          {METRICS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card flex gap-3 p-3.5">
              <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-cyan bg-cyan/10">
                <Icon className="h-4.5 w-4.5" />
              </span>
              <div className="space-y-0.5 min-w-0">
                <p className="text-sm font-bold text-text">{title}</p>
                <p className="text-xs text-text-muted leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Acceso">
        <div className="card flex gap-3 p-3.5">
          <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 text-text-dim bg-panel-elevated">
            <MessageCircleQuestion className="h-4.5 w-4.5" />
          </span>
          <div className="space-y-0.5">
            <p className="text-sm font-bold text-text">¿Problemas con tu cuenta?</p>
            <p className="text-xs text-text-muted leading-relaxed">
              Si tu cuenta no tiene acceso o tu suscripción venció, contacta con soporte desde el bot de Telegram.
            </p>
          </div>
        </div>
      </Section>

      <p className="text-[10px] text-text-dim text-center leading-relaxed px-4 pt-2">
        John Anthony analiza datos reales y explica sus decisiones -- no garantiza resultados. Juega con
        responsabilidad y dentro de tus posibilidades.
      </p>
    </div>
  )
}
